import React, { useMemo } from 'react';
import {
  BreakEvenChartWithLegend,
  BreakEvenRows,
} from 'components/RightPanel/panels/BreakEvenTables';
import LinkButton from 'components/common/LinkButton/LinkButton';
import { LINK_MULTI_YEAR_TREND_LABEL } from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { InputTaskCategory } from 'interfaces/IInputTask';
import { computeFinancialRisk } from './computeFinancialRisk';
import type { GmAreaBasis } from 'pages/FarmsPage/Workspace/Insights/computeGrossMargin';
import GrossMarginMatrix from './GrossMarginMatrix';

interface MarginBreakEvenViewProps {
  inputs: IInput[];
  activeCategory: InputTaskCategory;
  onCategoryChange: (category: InputTaskCategory) => void;
  gUnits: GUnit[];
  /** Opens this paddock's multi-year trend in Farm diagnostics. Omit when there's no paddock to open. */
  onOpenMultiYearTrend?: () => void;
  /** `total` at crop scope, where the plans sit on different paddocks — see GrossMarginMatrix. */
  areaBasis?: GmAreaBasis;
  /** Hides the matrix's Plan/Actual and value-mode controls — the plan preview (OP-2490). */
  readOnly?: boolean;
  /**
   * `breakpoint` (default) goes side by side from the lg screen width.
   * `wrap` puts break-even beside the matrix only when the space it's in
   * has room, else below it — the plan preview, whose width depends on its
   * contents panel rather than the window.
   */
  layout?: 'breakpoint' | 'wrap';
}

const LAYOUT_CLASSES = {
  breakpoint: {
    row: 'flex flex-col items-start gap-6 lg:flex-row lg:gap-8',
    breakEven:
      'flex w-full max-w-md flex-col gap-3 border-t border-gray-bright pt-6 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0',
  },
  // No divider: whether the two sit side by side isn't known, and the
  // Break-even heading already separates them.
  wrap: {
    row: 'flex flex-wrap items-start gap-x-8 gap-y-6',
    breakEven: 'flex min-w-[18rem] max-w-md flex-1 flex-col gap-3',
  },
};

/**
 * Insights "Margin & break-even" lens (OP-2393, formerly "Gross margin").
 *
 * The 3×3 gross margin matrix with the break-even yield/price tables and the
 * break-even chart beside it. Break-even comes from the same
 * computeFinancialRisk call and the same BreakEvenTables components as Live
 * totals → Financial risk, so on the Plan basis the two show identical
 * figures for the same scope. The Plan/Actual toggle here drives both the
 * matrix and the break-even section.
 */
const MarginBreakEvenView: React.FC<MarginBreakEvenViewProps> = ({
  inputs,
  activeCategory,
  onCategoryChange,
  gUnits,
  onOpenMultiYearTrend,
  areaBasis,
  readOnly = false,
  layout = 'breakpoint',
}) => {
  const categories = useMemo(() => new Set([activeCategory]), [activeCategory]);
  const risk = useMemo(
    () => computeFinancialRisk(inputs, gUnits, categories),
    [inputs, gUnits, categories]
  );

  const basisLabel =
    activeCategory === InputTaskCategory.Plan ? 'Plan' : 'Actual';

  return (
    // Stacked on narrow screens, side by side from lg. A subtle divider
    // (not a raised card) separates the two sections — top border when
    // stacked, left border when side by side.
    <div className="flex flex-col items-start gap-4">
      {onOpenMultiYearTrend && (
        <LinkButton onClick={onOpenMultiYearTrend}>
          {LINK_MULTI_YEAR_TREND_LABEL}
        </LinkButton>
      )}
      <div className={LAYOUT_CLASSES[layout].row}>
        <GrossMarginMatrix
          inputs={inputs}
          activeCategory={activeCategory}
          onCategoryChange={onCategoryChange}
          gUnits={gUnits}
          areaBasis={areaBasis}
          readOnly={readOnly}
        />

        <section
          id="margin-break-even"
          aria-labelledby="margin-break-even-title"
          className={LAYOUT_CLASSES[layout].breakEven}
        >
          <div>
            <h3
              id="margin-break-even-title"
              className="text-base font-bold text-charcoal"
            >
              Break-even
            </h3>
            <p className="text-sm text-gray-dark">
              {basisLabel} tasks — the yield or price needed to cover input spend
            </p>
          </div>
          {risk.hasData ? (
            <div className="flex flex-col gap-4">
              <table className="w-full text-base">
                <tbody>
                  <BreakEvenRows risk={risk} />
                </tbody>
              </table>
              <BreakEvenChartWithLegend risk={risk} />
            </div>
          ) : (
            <p className="text-base text-gray-dark">
              No break-even yet — add production (yield and price) and cost
              steps to the {basisLabel.toLowerCase()} tasks.
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

export default MarginBreakEvenView;
